import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useTypedSelector } from "../../app/store";
import {
  createContractSecurityApi,
  isAxiosErrorProblemDetails,
} from "../../api";
import { ContractSecurityViewModel } from "../../api/axcloud";
import { showError, showSuccess } from "../alerts/alertsSlice";

export function useIpAddressRestriction() {
  const dispatch = useAppDispatch();
  const accessToken = useTypedSelector((x) => x.login.accessToken);
  const [saved, setSaved] = useState<ContractSecurityViewModel>();
  const [draft, setDraft] = useState<ContractSecurityViewModel>();
  const [status, setStatus] = useState<
    "loading" | "ready" | "failed" | "saving"
  >("loading");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const requestScope = useRef({ active: false });

  useEffect(() => {
    const scope = { active: true };
    requestScope.current = scope;
    setSaved(undefined);
    setDraft(undefined);
    setStatus("loading");
    if (accessToken) {
      createContractSecurityApi(accessToken)
        .getContractSecurity()
        .then(
          ({ data }) => {
            if (scope.active) {
              setSaved(data);
              setDraft(data);
              setStatus("ready");
            }
          },
          () => {
            if (scope.active) setStatus("failed");
          }
        );
    }
    return () => {
      scope.active = false;
    };
  }, [accessToken, loadAttempt]);

  const modified = !!saved && JSON.stringify(saved) !== JSON.stringify(draft);
  useEffect(() => {
    if (!modified) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [modified]);

  const save = useCallback(async () => {
    if (!accessToken || !draft || !modified || status !== "ready") return;
    const scope = requestScope.current;
    setStatus("saving");
    try {
      const { data } = await createContractSecurityApi(
        accessToken
      ).updateContractSecurity({
        isIpAddressRestrictionEnabled: draft.isIpAddressRestrictionEnabled,
        ipAddresses: (draft.ipAddresses ?? []).map((x) => ({ ipAddress: x.ipAddress!, memo: x.memo })),
      });
      if (!scope.active) return;
      setSaved(data);
      setDraft(data);
      dispatch(showSuccess("IPアドレス制限の変更を保存しました。"));
    } catch (error) {
      if (!scope.active) return;
      dispatch(
        showError(
          isAxiosErrorProblemDetails(error)
            ? error.response?.data.detail ??
                "IPアドレス制限を保存できませんでした。"
            : "IPアドレス制限を保存できませんでした。"
        )
      );
    } finally {
      if (scope.active) setStatus("ready");
    }
  }, [accessToken, dispatch, draft, modified, status]);

  return {
    draft,
    setDraft,
    status,
    modified,
    save,
    discard: () => setDraft(saved),
    retry: () => setLoadAttempt((x) => x + 1),
  };
}
